import { z } from 'zod';

/**
 * Roteiro do culto (M10). A ordem do serviço com duração de cada item e o
 * horário calculado em cascata — é a folha que o dirigente segura no domingo.
 */

export const SERVICE_ITEM_KINDS = [
  'WORSHIP',
  'WORD',
  'OFFERING',
  'ANNOUNCEMENT',
  'VIDEO',
  'PRAYER',
  'COMMUNION',
  'PERFORMANCE',
  'OTHER',
] as const;
export const serviceItemKindSchema = z.enum(SERVICE_ITEM_KINDS);
export type ServiceItemKind = z.infer<typeof serviceItemKindSchema>;

export const SERVICE_ITEM_KIND_LABEL: Record<ServiceItemKind, string> = {
  WORSHIP: 'Louvor',
  WORD: 'Palavra',
  OFFERING: 'Oferta',
  ANNOUNCEMENT: 'Avisos',
  VIDEO: 'Vídeo',
  PRAYER: 'Oração',
  COMMUNION: 'Ceia',
  PERFORMANCE: 'Apresentação',
  OTHER: 'Outro',
};

/** Cor do selo de cada tipo, na paleta do produto. */
export const SERVICE_ITEM_KIND_TOM: Record<ServiceItemKind, string> = {
  WORSHIP: 'marca',
  WORD: 'convocado',
  OFFERING: 'aguardando',
  ANNOUNCEMENT: 'neutro',
  VIDEO: 'neutro',
  PRAYER: 'confirmado',
  COMMUNION: 'substituido',
  PERFORMANCE: 'marca',
  OTHER: 'neutro',
};

const duracao = z.coerce
  .number()
  .int()
  .min(0, 'A duração não pode ser negativa')
  .max(600, 'Duração longa demais para um item de roteiro');

export const createServiceItemSchema = z.object({
  title: z.string().trim().min(2, 'Informe o que acontece neste item').max(160),
  kind: serviceItemKindSchema.default('OTHER'),
  durationMinutes: duracao.default(5),
  notes: z.string().trim().max(1000).nullish(),
  personId: z.string().uuid().nullish(),
  ministryId: z.string().uuid().nullish(),
  /** Item de louvor ligado ao repertório, com o tom deste culto (M11). */
  songId: z.string().uuid().nullish(),
  songKey: z.string().trim().max(10).nullish(),
  /** Onde entrar na ordem. Vazio = no fim. */
  position: z.coerce.number().int().min(1).max(200).nullish(),
});
export type CreateServiceItemInput = z.infer<typeof createServiceItemSchema>;

export const updateServiceItemSchema = createServiceItemSchema.partial();
export type UpdateServiceItemInput = z.infer<typeof updateServiceItemSchema>;

export const reorderServiceItemsSchema = z.object({
  /** Os ids na ordem em que devem ficar. Precisa trazer o roteiro inteiro. */
  ids: z.array(z.string().uuid()).min(1).max(200),
});
export type ReorderServiceItemsInput = z.infer<typeof reorderServiceItemsSchema>;

export const serviceItemSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  position: z.number().int(),
  title: z.string(),
  kind: serviceItemKindSchema,
  durationMinutes: z.number().int(),
  notes: z.string().nullable(),
  personId: z.string().uuid().nullable(),
  personName: z.string().nullable(),
  ministryId: z.string().uuid().nullable(),
  ministryName: z.string().nullable(),
  songId: z.string().uuid().nullable(),
  songTitle: z.string().nullable(),
  songKey: z.string().nullable(),
  /** Horário calculado em cascata, no fuso da igreja: "18:12". */
  startsAt: z.string(),
  endsAt: z.string(),
  filesCount: z.number().int(),
});
export type ServiceItem = z.infer<typeof serviceItemSchema>;

export const servicePlanSchema = z.object({
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  eventStartsAt: z.string().datetime(),
  eventEndsAt: z.string().datetime(),
  items: z.array(serviceItemSchema),
  /** Soma das durações, e quanto o culto tem de janela. */
  totalMinutes: z.number().int(),
  plannedMinutes: z.number().int(),
  /** Quanto passou da janela do culto. Zero quando cabe. */
  overflowMinutes: z.number().int(),
});
export type ServicePlan = z.infer<typeof servicePlanSchema>;

// ── Modelos de roteiro ───────────────────────────────────────────────────────

export const createPlanTemplateSchema = z.object({
  name: z.string().trim().min(2, 'Dê um nome ao modelo').max(120),
  description: z.string().trim().max(500).nullish(),
  eventTypeId: z.string().uuid().nullish(),
  items: z
    .array(
      z.object({
        title: z.string().trim().min(2).max(160),
        kind: serviceItemKindSchema.default('OTHER'),
        durationMinutes: duracao.default(5),
        ministryId: z.string().uuid().nullish(),
      }),
    )
    .min(1, 'Um modelo precisa de pelo menos um item')
    .max(100),
});
export type CreatePlanTemplateInput = z.infer<typeof createPlanTemplateSchema>;

export const planTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  eventTypeId: z.string().uuid().nullable(),
  eventTypeName: z.string().nullable(),
  totalMinutes: z.number().int(),
  items: z.array(
    z.object({
      title: z.string(),
      kind: serviceItemKindSchema,
      durationMinutes: z.number().int(),
      ministryId: z.string().uuid().nullable(),
      ministryName: z.string().nullable(),
    }),
  ),
});
export type PlanTemplate = z.infer<typeof planTemplateSchema>;

export const applyPlanTemplateSchema = z.object({
  templateId: z.string().uuid(),
  /** Substituir o que já existe, ou acrescentar no fim. */
  substituir: z.boolean().default(false),
});
export type ApplyPlanTemplateInput = z.infer<typeof applyPlanTemplateSchema>;

// ── Modo apresentação ────────────────────────────────────────────────────────

/** O que o dirigente manda para os celulares da equipe, ao vivo. */
export const presentationStateSchema = z.object({
  eventId: z.string().uuid(),
  /** Índice do item atual. `-1` = ainda não começou. */
  currentIndex: z.number().int(),
  /** Quando o item atual começou, para o cronômetro contar. */
  startedAt: z.string().datetime().nullable(),
  /** Quem está dirigindo. Só ele muda o item. */
  driverName: z.string().nullable(),
  running: z.boolean(),
});
export type PresentationState = z.infer<typeof presentationStateSchema>;

export const presentationCommandSchema = z.object({
  eventId: z.string().uuid(),
  acao: z.enum(['iniciar', 'proximo', 'anterior', 'ir', 'pausar', 'encerrar']),
  /** Para `ir`: o índice do item. */
  index: z.coerce.number().int().min(0).max(199).nullish(),
});
export type PresentationCommandInput = z.infer<typeof presentationCommandSchema>;
